// PRIME-SHELL-UX-003 — shared dismissal behavior for every top-bar-owned
// dropdown menu (Workspace, Help, Persona, Profile).
//
// Product intent (Benji, verbatim): dismissal must feel calm and predictable.
// "not like one second... if you accidentally go outside of a menu navigation
// [and] it automatically closes, I hate that." So a brief pointer excursion
// outside must NOT close the menu; a menu only auto-closes after a full
// inactivity interval (~3-4s) with nothing happening inside it.
//
// The hook owns four dismissal paths, all funneled through ONE module-level
// controller ("bus") so that at most one menu is ever open and exactly one
// shared set of document listeners is attached while any menu is open
// (AC-08 — never one listener set per menu):
//
//   1. Outside pointerdown (capture phase) closes the open menu and swallows
//      the initiating gesture so the underlying control is NOT activated
//      (AC-01 — no click-through). A pointerdown that lands on another managed
//      menu's trigger is deliberately let through so that trigger's own click
//      can open it (mutual exclusion then closes the first).
//   2. Escape closes and returns focus to the trigger (AC-02), falling back to
//      the topbar container if the trigger has unmounted.
//   3. Route change closes any open menu (AC-04) — handled per-hook via the
//      router location, since the bus is router-agnostic.
//   4. Inactivity: a MENU_INACTIVITY_MS timer that starts on open, PAUSES
//      while the pointer is over or focus is inside the trigger/popup, and
//      RESETS on any pointer/keyboard activity inside (AC-05/AC-06).
//
// Opening any managed menu closes the currently-open one (AC-03).
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

// Locked by the coordinator: three-to-four seconds, explicitly "not like one
// second". 4000ms is the single source of truth for the inactivity interval.
export const MENU_INACTIVITY_MS = 4000;

// ---------------------------------------------------------------------------
// Module-level controller (the shared "bus").
//
// Only ONE managed menu can be active at a time, so a single active id plus a
// single shared listener set and a single inactivity timer are all that is
// ever needed. Every mounted menu registers a descriptor so the bus can (a)
// recognise clicks that land on *another* menu's trigger and (b) drive mutual
// exclusion and focus restoration through the correct hook instance.
// ---------------------------------------------------------------------------
const registered = new Map(); // id -> descriptor
let activeId = null;
let listenersAttached = false;
let hovering = false;
let focusInside = false;
let inactivityTimer = null;
let menuIdSeq = 0;

function nextMenuId() {
  menuIdSeq += 1;
  return `dm-${menuIdSeq}`;
}

function activeDescriptor() {
  return activeId != null ? registered.get(activeId) || null : null;
}

function activeInactivityMs() {
  return activeDescriptor()?.inactivityMs ?? MENU_INACTIVITY_MS;
}

function nodeInside(descriptor, node) {
  if (!descriptor || !node) return false;
  const trigger = descriptor.getTrigger();
  const popup = descriptor.getPopup();
  return Boolean((trigger && trigger.contains(node)) || (popup && popup.contains(node)));
}

function isInsideActive(node) {
  return nodeInside(activeDescriptor(), node);
}

function isInsideAnyTrigger(node) {
  if (!node) return false;
  for (const descriptor of registered.values()) {
    const trigger = descriptor.getTrigger();
    if (trigger && trigger.contains(node)) return true;
  }
  return false;
}

function clearInactivityTimer() {
  if (inactivityTimer != null) {
    clearTimeout(inactivityTimer);
    inactivityTimer = null;
  }
}

// Single entry point for the pause/reset semantics: clearing then (unless
// paused) rescheduling means "reset on activity" and "pause on hover/focus"
// are the same one-liner from every call site.
function refreshInactivityTimer() {
  clearInactivityTimer();
  if (activeId == null) return;
  if (hovering || focusInside) return; // paused
  inactivityTimer = setTimeout(() => {
    const descriptor = activeDescriptor();
    if (descriptor) descriptor.requestClose('inactivity');
  }, activeInactivityMs());
}

// Swallow the click that trails an outside pointerdown so the underlying
// control does not activate (AC-01). Capture + stopImmediatePropagation stops
// the event before React's delegated root listener ever sees it.
//
// The guard is tracked (never a bare `once` listener) so it can be torn down
// deterministically: adding a new one supersedes the old, the guard removes
// itself once the trailing click fires, and a short safety timeout drops it if
// NO click trails the gesture (e.g. a drag or text selection outside the
// menu) — otherwise a stale guard could eat an unrelated later click.
let pendingClickSwallow = null;
let pendingClickSwallowTimer = null;

function clearPendingClickSwallow() {
  if (pendingClickSwallow) {
    document.removeEventListener('click', pendingClickSwallow, true);
    pendingClickSwallow = null;
  }
  if (pendingClickSwallowTimer != null) {
    clearTimeout(pendingClickSwallowTimer);
    pendingClickSwallowTimer = null;
  }
}

function swallowNextClick() {
  clearPendingClickSwallow();
  const onClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === 'function') {
      event.stopImmediatePropagation();
    }
    clearPendingClickSwallow();
  };
  pendingClickSwallow = onClick;
  document.addEventListener('click', onClick, { capture: true });
  pendingClickSwallowTimer = setTimeout(clearPendingClickSwallow, 300);
}

function onDocumentPointerDown(event) {
  if (activeId == null) return;
  const { target } = event;
  if (isInsideActive(target)) {
    refreshInactivityTimer(); // interacting with the menu keeps it alive
    return;
  }
  if (isInsideAnyTrigger(target)) {
    // Another managed menu's trigger — let its click open it; mutual exclusion
    // closes the current menu. Do NOT swallow.
    return;
  }
  // Genuine outside gesture: close and suppress click-through.
  event.preventDefault();
  event.stopPropagation();
  swallowNextClick();
  const descriptor = activeDescriptor();
  if (descriptor) descriptor.requestClose('outside');
}

function onDocumentKeyDown(event) {
  if (activeId == null) return;
  if (event.key === 'Escape') {
    event.stopPropagation();
    const descriptor = activeDescriptor();
    if (descriptor) descriptor.requestClose('escape');
    return;
  }
  if (isInsideActive(event.target)) refreshInactivityTimer();
}

function onDocumentPointerMove(event) {
  if (activeId == null) return;
  if (isInsideActive(event.target)) refreshInactivityTimer();
}

function onDocumentPointerOver(event) {
  if (activeId == null) return;
  if (isInsideActive(event.target)) {
    hovering = true;
    refreshInactivityTimer();
  }
}

function onDocumentPointerOut(event) {
  if (activeId == null) return;
  // Only treat this as "left the menu" when we move from inside to a node that
  // is NOT inside the active menu (child-boundary crossings stay hovered).
  if (isInsideActive(event.target) && !isInsideActive(event.relatedTarget)) {
    hovering = false;
    refreshInactivityTimer();
  }
}

function onDocumentFocusIn(event) {
  if (activeId == null) return;
  const inside = isInsideActive(event.target);
  if (inside && !focusInside) {
    focusInside = true;
    refreshInactivityTimer();
  } else if (!inside && focusInside) {
    focusInside = false;
    refreshInactivityTimer();
  }
}

function onDocumentFocusOut(event) {
  if (activeId == null) return;
  if (isInsideActive(event.target) && !isInsideActive(event.relatedTarget)) {
    focusInside = false;
    refreshInactivityTimer();
  }
}

function attachSharedListeners() {
  if (listenersAttached) return;
  document.addEventListener('pointerdown', onDocumentPointerDown, true);
  document.addEventListener('keydown', onDocumentKeyDown, true);
  document.addEventListener('pointermove', onDocumentPointerMove, true);
  document.addEventListener('pointerover', onDocumentPointerOver, true);
  document.addEventListener('pointerout', onDocumentPointerOut, true);
  document.addEventListener('focusin', onDocumentFocusIn, true);
  document.addEventListener('focusout', onDocumentFocusOut, true);
  listenersAttached = true;
}

function detachSharedListeners() {
  if (!listenersAttached) return;
  document.removeEventListener('pointerdown', onDocumentPointerDown, true);
  document.removeEventListener('keydown', onDocumentKeyDown, true);
  document.removeEventListener('pointermove', onDocumentPointerMove, true);
  document.removeEventListener('pointerover', onDocumentPointerOver, true);
  document.removeEventListener('pointerout', onDocumentPointerOut, true);
  document.removeEventListener('focusin', onDocumentFocusIn, true);
  document.removeEventListener('focusout', onDocumentFocusOut, true);
  listenersAttached = false;
}

function busOpen(id) {
  if (activeId != null && activeId !== id) {
    const previous = registered.get(activeId);
    if (previous) previous.requestClose('superseded');
  }
  activeId = id;
  hovering = false;
  focusInside = false;
  attachSharedListeners();
  refreshInactivityTimer();
}

function busClose(id) {
  if (activeId !== id) return;
  activeId = null;
  hovering = false;
  focusInside = false;
  clearInactivityTimer();
  detachSharedListeners();
}

// Test-only: reset the module singleton between cases so leaked state from one
// test never bleeds into the next.
export function __resetDismissableMenuBus() {
  clearInactivityTimer();
  clearPendingClickSwallow();
  detachSharedListeners();
  registered.clear();
  activeId = null;
  hovering = false;
  focusInside = false;
  menuIdSeq = 0;
}

/**
 * useDismissableMenu — wire a single top-bar dropdown into the shared bus.
 *
 * Returns `open` plus imperative `toggle`/`open`-style setters and the refs to
 * attach to the trigger button and the popup container. The hook owns the open
 * state so mutual exclusion, the inactivity timer, and focus restoration can be
 * driven centrally.
 */
export function useDismissableMenu(options = {}) {
  const inactivityMs = options.inactivityMs ?? MENU_INACTIVITY_MS;
  const [open, setOpenState] = useState(false);
  const triggerRef = useRef(null);
  const popupRef = useRef(null);
  const idRef = useRef(null);
  if (idRef.current == null) idRef.current = nextMenuId();

  // Always-current focus-restoration closure (kept in a ref so the descriptor
  // registered once on mount never goes stale).
  const restoreFocusRef = useRef(() => {});
  restoreFocusRef.current = () => {
    const trigger = triggerRef.current;
    if (trigger && typeof trigger.focus === 'function' && document.contains(trigger)) {
      trigger.focus();
      return;
    }
    // Trigger unmounted — fall back to the topbar container (never body).
    const topbar = document.querySelector('[data-testid="app-topbar"]');
    if (topbar && typeof topbar.focus === 'function') {
      if (!topbar.hasAttribute('tabindex')) topbar.setAttribute('tabindex', '-1');
      topbar.focus();
    }
  };

  // Register/unregister this menu's descriptor with the bus exactly once.
  useEffect(() => {
    const id = idRef.current;
    const descriptor = {
      id,
      inactivityMs,
      getTrigger: () => triggerRef.current,
      getPopup: () => popupRef.current,
      requestClose: (reason) => {
        setOpenState(false);
        if (reason === 'escape') restoreFocusRef.current();
      },
    };
    registered.set(id, descriptor);
    return () => {
      registered.delete(id);
      busClose(id);
    };
    // Descriptor closures read live refs; inactivityMs is a constant per menu.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Drive the bus from the open state (mutual exclusion + shared listeners +
  // inactivity timer all live behind busOpen/busClose).
  useEffect(() => {
    const id = idRef.current;
    if (open) busOpen(id);
    else busClose(id);
  }, [open]);

  // Route change closes any open menu (AC-04). Skip the initial mount so a menu
  // that is opened and then triggers navigation is the only thing that closes
  // it — not the first render.
  const { pathname } = useLocation();
  const firstPathRef = useRef(true);
  useEffect(() => {
    if (firstPathRef.current) {
      firstPathRef.current = false;
      return;
    }
    setOpenState(false);
  }, [pathname]);

  const setOpen = useCallback((next) => setOpenState(Boolean(next)), []);
  const toggle = useCallback(() => setOpenState((prev) => !prev), []);
  const close = useCallback(() => setOpenState(false), []);

  return {
    open,
    setOpen,
    toggle,
    close,
    triggerRef,
    popupRef,
    triggerProps: {
      ref: triggerRef,
      'aria-haspopup': 'menu',
      'aria-expanded': open,
    },
    popupProps: {
      ref: popupRef,
    },
  };
}
